/**
 * Layout for blank music staff paper.
 *
 * A staff is five lines one staff space apart, so everything scales from a single number:
 * the staff space in millimetres. Proportions follow common engraving practice — staff
 * lines about 0.13 of a space thick, barlines about 0.16 — and the clefs are drawn in
 * staff-space units from the staff's top line, so they scale with it.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";

export type Clef = "treble" | "bass" | "grand" | "none";

export interface StaffOptions {
  clef: Clef;
  /** The distance between staff lines, in millimetres. */
  space: number;
  /** Systems wanted on the page; 0 fits as many as will go. */
  systems: number;
  /** Measures per system (equal widths); 0 leaves the staff without barlines. */
  measures: number;
  showClef: boolean;
  /** Reserve room at the top of the page for title and composer lines. */
  header: boolean;
}

export const STAFF_LINES = 5;
/** Staff-space clearance between systems, so ledger lines and notes fit. */
const MIN_SYSTEM_GAP = 7;
/** Space between the two staves of a grand staff: room for middle C and ledger lines. */
export const GRAND_GAP = 8;
const MAX_GAP_FACTOR = 3;
export const HEADER_HEIGHT = 22;
export const MIN_SPACE = 1.5;
export const MAX_SPACE = 9;

/** Line thickness in millimetres: 0.13 of a space, never hairline thin or heavy. */
export function lineThickness(space: number): number {
  return r2(Math.min(0.6, Math.max(0.18, space * 0.13)));
}

/** Barline thickness in millimetres: 0.16 of a space, a little heavier than a staff line. */
export function barThickness(space: number): number {
  return r2(Math.min(0.75, Math.max(0.24, space * 0.16)));
}

/** Height of one system, in millimetres. */
export function systemHeight(clef: Clef, space: number): number {
  return clef === "grand"
    ? space * (4 + GRAND_GAP + 4)
    : space * (STAFF_LINES - 1);
}

export interface Staff {
  /** y of the top line. */
  top: number;
  clef: "treble" | "bass" | "none";
}

export interface System {
  staves: Staff[];
  /** y of the top line of the first staff, and the bottom line of the last. */
  y1: number;
  y2: number;
  /** x of the left end and of the right end of the lines. */
  left: number;
  right: number;
  /** x of each barline after the left one. */
  barlines: number[];
  /** A brace for a grand staff. */
  brace: { x: number; y1: number; y2: number; width: number } | null;
  /** x of the clef's origin (see `CLEF_ORIGIN`). */
  clefX: number;
}

export interface StaffPage {
  systems: System[];
  /** The most systems that fit this page at this size. */
  max: number;
  /** False when more systems were asked for than fit. */
  fits: boolean;
}

/** Where the clef's drawing origin sits, as a multiple of the staff space from the left. */
const CLEF_LEAD: Record<"treble" | "bass", number> = { treble: 1.5, bass: 1 };
/** Width reserved for a clef, in staff spaces. */
const CLEF_ROOM = 4.4;
const NO_CLEF_ROOM = 1.2;
const BRACE_WIDTH = 2;

/** How many systems of this size fit in `available` mm. */
export function systemsThatFit(
  available: number,
  height: number,
  space: number
): number {
  const gap = MIN_SYSTEM_GAP * space;
  return Math.max(1, Math.floor((available + gap) / (height + gap)));
}

/** The staves of one system whose first top line is at `y1`. */
function stavesFor(options: StaffOptions, y1: number): Staff[] {
  const { space } = options;
  if (options.clef === "grand") {
    return [
      { top: r2(y1), clef: options.showClef ? "treble" : "none" },
      {
        top: r2(y1 + space * (4 + GRAND_GAP)),
        clef: options.showClef ? "bass" : "none",
      },
    ];
  }
  const clef =
    options.showClef && options.clef !== "none" ? options.clef : "none";
  return [{ top: r2(y1), clef }];
}

function buildSystem(
  geometry: PageGeometry,
  options: StaffOptions,
  y1: number,
  left: number,
  right: number
): System {
  const { space } = options;
  const staves = stavesFor(options, y1);
  const first = staves[0]?.clef ?? "none";
  const room = (first === "none" ? NO_CLEF_ROOM : CLEF_ROOM) * space;
  const start = left + room;
  const barlines: number[] = [];
  for (let k = 1; k <= options.measures; k += 1) {
    barlines.push(r2(start + ((right - start) * k) / options.measures));
  }
  const y2 = y1 + systemHeight(options.clef, space);
  return {
    staves,
    y1: r2(y1),
    y2: r2(y2),
    left: r2(left),
    right: r2(right),
    barlines,
    brace:
      options.clef === "grand"
        ? {
            x: r2(geometry.left),
            y1: r2(y1),
            y2: r2(y2),
            width: r2(BRACE_WIDTH * space),
          }
        : null,
    clefX: r2(left + CLEF_LEAD[first === "bass" ? "bass" : "treble"] * space),
  };
}

/** A clef must stay at least this far, in millimetres, from the paper's edge. */
const SAFE_EDGE = 6;

/**
 * How far the clefs reach above the first staff and below the last, in staff spaces. The
 * treble clef's top loop rises about 2.4 spaces above the staff and its tail drops about
 * 1.7 below it, which on a large staff would run into or past the page margin.
 */
export function clefReach(options: StaffOptions): {
  above: number;
  below: number;
} {
  if (!options.showClef) {
    return { above: 0, below: 0 };
  }
  switch (options.clef) {
    case "treble":
      return { above: 2.4, below: 1.7 };
    case "grand":
      return { above: 2.4, below: 0.5 };
    case "bass":
      return { above: 0.5, below: 0.5 };
    default:
      return { above: 0, below: 0 };
  }
}

/** One page of staves. */
export function layoutStaffPage(
  geometry: PageGeometry,
  options: StaffOptions,
  headerHeight: number
): StaffPage {
  const { space } = options;
  const height = systemHeight(options.clef, space);
  const reach = clefReach(options);
  const room = Math.max(0, geometry.margin - SAFE_EDGE);
  const extraTop =
    headerHeight > 0 ? 0 : Math.max(0, reach.above * space - room);
  const extraBottom = Math.max(0, reach.below * space - room);
  const available =
    geometry.contentHeight - headerHeight - extraTop - extraBottom;
  const max = systemsThatFit(available, height, space);
  const count = options.systems > 0 ? Math.min(options.systems, max) : max;
  const minGap = MIN_SYSTEM_GAP * space;
  const spread = count > 1 ? (available - count * height) / (count - 1) : 0;
  const gap = Math.min(spread, minGap * MAX_GAP_FACTOR);
  const grand = options.clef === "grand";
  const indent = grand ? BRACE_WIDTH * space + 1 : 0;
  const left = geometry.left + indent;
  const right = geometry.left + geometry.contentWidth;
  const top = geometry.top + headerHeight + extraTop;

  const systems: System[] = Array.from({ length: count }, (_, index) =>
    buildSystem(geometry, options, top + index * (height + gap), left, right)
  );
  return { systems, max, fits: options.systems <= 0 || options.systems <= max };
}

/** The five line positions of a staff, top to bottom. */
export function staffLines(top: number, space: number): number[] {
  return Array.from({ length: STAFF_LINES }, (_, i) => r2(top + i * space));
}

/* --------------------------------------------------------------------- shapes */

/**
 * Treble (G) clef, a single stroke drawn in staff-space units: y = 0 is the top line, the
 * G line it winds around is y = 3. Stroke it with round caps.
 */
export const TREBLE_CLEF_PATH =
  "M0.05 5.45C-0.5 5.75-1 5.35-0.75 4.8C-0.55 4.4-0.05 4.45 0 4.85C0.05 5.2 0.4 4.6 0.62 3.8C0.9 2.7 0.95 0.5 0.8-0.9C0.7-1.7 0.95-2.3 1.25-2.3C1.75-2.3 1.7-1.2 1-0.1C0.4 1.3-0.9 2-0.7 3.1C-0.5 4 0.9 4.1 1.35 3.1C1.75 2.2 0.7 1.4 0.1 1.9C-0.45 2.4-0.2 3.3 0.35 3.3C0.75 3.3 0.8 2.6 0.45 2.6";

/** Bass (F) clef curve; the dot on the F line (y = 1) and the two dots are drawn separately. */
export const BASS_CLEF_PATH =
  "M-0.05 1C0.15-0.35 2.2-0.45 2.3 1.5C2.35 3 1 3.7-0.45 4.3";

/** Treble clef scale about its G line, so it spans about seven staff spaces. */
export const TREBLE_SCALE = 0.9;

/**
 * A curly brace from (x, y1) down to (x, y2), `width` wide, opening to the right: the
 * point sits at mid-height. Built from two mirrored cubic curves.
 */
export function bracePath(
  x: number,
  y1: number,
  y2: number,
  width: number
): string {
  const mid = (y1 + y2) / 2;
  const w = width;
  const left = r2(x);
  const right = r2(x + w);
  const q = r2(x + w * 0.25);
  const top = r2(y1);
  const bottom = r2(y2);
  const m = r2(mid);
  const upper = r2(y1 + (mid - y1) * 0.55);
  const lower = r2(y2 - (y2 - mid) * 0.55);
  return [
    `M${right} ${top}`,
    `C${q} ${r2(y1 + (mid - y1) * 0.12)} ${right} ${upper} ${q} ${upper}`,
    `C${left} ${upper} ${left} ${r2(m - (mid - y1) * 0.1)} ${left} ${m}`,
    `C${left} ${r2(m + (y2 - mid) * 0.1)} ${left} ${lower} ${q} ${lower}`,
    `C${right} ${lower} ${q} ${r2(y2 - (y2 - mid) * 0.12)} ${right} ${bottom}`,
  ].join("");
}
